import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment,
  Paper,
  Typography,
} from "@mui/material";
import {
  Add,
  Search,
  FilterList,
  MoreVert,
  Visibility,
  Edit,
  Delete,
} from "@mui/icons-material";
import AddWallet from "./AddWallet";

function Wallet() {
  // State for data and UI controls
  const [wallets, setWallets] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedWalletId, setSelectedWalletId] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);

  // Handle menu open/close
  const handleMenuClick = (event, walletId) => {
    setAnchorEl(event.currentTarget);
    setSelectedWalletId(walletId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedWalletId(null);
  };

  // Action handlers
  const handleView = () => {
    console.log("View wallet:", selectedWalletId);
    handleMenuClose();
  };

  const handleEdit = () => {
    console.log("Edit wallet:", selectedWalletId);
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log("Delete wallet:", selectedWalletId);
    handleMenuClose();
  };

  // Table pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleAddWalletClick = () => {
    setOpenAddDialog(true);
  };

  const handleAddDialogClose = () => {
    setOpenAddDialog(false);
  };

  // Fetch wallet data on component mount
  useEffect(() => {
    const fetchWallets = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://dummyjson.com/users?limit=20');
        const data = await response.json();
        
        // Format data for wallet table - removing balance and transactions
        const formattedData = data.users.map(user => ({
          id: `WAL-${user.id.toString().padStart(4, '0')}`,
          amount: `$${(Math.random() * 5000).toFixed(2)}`,
          name: `${user.firstName} ${user.lastName}`
        }));
        
        setWallets(formattedData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWallets();
  }, []);

  // Filter wallets based on search query
  const filteredWallets = wallets.filter(
    (wallet) =>
      wallet.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      wallet.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayedWallets = filteredWallets.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: "#212121" }}>
          Wallet Management
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" mb={3}>
        <TextField
          placeholder="Search wallets..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            width: 300,
            bgcolor: "#ffffff",
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#e0e0e0" },
              "&:hover fieldset": { borderColor: "#bdbdbd" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{
              borderColor: "#e0e0e0",
              color: "#424242",
              "&:hover": {
                borderColor: "#9e9e9e",
                bgcolor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Filters
          </Button>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddWalletClick}
            sx={{
              bgcolor: "#4285F4",
              color: "white",
              "&:hover": {
                bgcolor: "#3367D6",
              },
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Add Wallet
          </Button>
        </Box>
      </Box>

      <Card sx={{ boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)", bgcolor: "#ffffff" }}>
        <TableContainer component={Paper} elevation={0} sx={{ bgcolor: "#ffffff" }}>
          <Table>
            <TableHead sx={{ bgcolor: "#f9fafc" }}>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  Wallet ID
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  Amount
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ffffff" }}>
              {loading ? (
                <TableRow sx={{ bgcolor: "#ffffff" }}>
                  <TableCell colSpan={4} align="center" sx={{ py: 3, color: "#212121" }}>
                    Loading wallets...
                  </TableCell>
                </TableRow>
              ) : displayedWallets.length === 0 ? (
                <TableRow sx={{ bgcolor: "#ffffff" }}>
                  <TableCell colSpan={4} align="center" sx={{ py: 3, color: "#212121" }}>
                    No wallets found
                  </TableCell>
                </TableRow>
              ) : (
                displayedWallets.map((wallet) => (
                  <TableRow
                    key={wallet.id}
                    sx={{
                      bgcolor: "#ffffff",
                      "&:hover": { bgcolor: "#f5f5f5" },
                      cursor: "pointer",
                    }}
                  >
                    <TableCell sx={{ color: "#212121" }}>
                      {wallet.id}
                    </TableCell>
                    <TableCell sx={{ color: "#212121", fontWeight: 500 }}>
                      {wallet.amount}
                    </TableCell>
                    <TableCell sx={{ color: "#212121" }}>
                      {wallet.name}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, wallet.id)}
                        sx={{ color: "#757575" }}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredWallets.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #e0e0e0",
              bgcolor: "#ffffff",
              color: "#212121",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                color: "#212121",
              },
              "& .MuiTablePagination-select": {
                color: "#212121",
              },
              "& .MuiTablePagination-selectIcon": {
                color: "#757575",
              },
              "& .MuiIconButton-root": {
                color: "#757575",
              },
            }}
          />
        </TableContainer>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
            minWidth: 120,
            bgcolor: "#ffffff",
          },
        }}
      >
        <MenuItem onClick={handleView} sx={{ color: "#212121" }}>
          <Visibility fontSize="small" sx={{ mr: 1, color: "#4285F4" }} /> View
        </MenuItem>
        <MenuItem onClick={handleEdit} sx={{ color: "#212121" }}>
          <Edit fontSize="small" sx={{ mr: 1, color: "#4285F4" }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "#212121" }}>
          <Delete fontSize="small" sx={{ mr: 1, color: "#f44336" }} /> Delete
        </MenuItem>
      </Menu>

      {/* Add Wallet Dialog */}
      <AddWallet open={openAddDialog} onClose={handleAddDialogClose} />
    </Box>
  );
}

export default Wallet;